import * as d3 from 'd3'

import type { Simulation } from 'd3'

import { type BubbleData } from '../components/BubbleChart'
import { DetailedBudget } from '../../core'

// D3 SVG Types
export type SimulationType = Simulation<BubbleData, undefined>

const PROGRESS_INDICATOR_SIZE = 5
const MAX_BUBBLE_RADIUS = 100
const MIN_BUBBLE_RADIUS = 25

/**
 * Enables dragging of bubbles
 * @param simulation D3 Simulation
 */
export const drag = (simulation: SimulationType) => {
  const dragStarted = (event: any) => {
    if (!event.active) simulation.alphaTarget(0.3).restart()
    event.subject.fx = event.subject.x
    event.subject.fy = event.subject.y
  }

  const dragged = (event: any) => {
    event.subject.fx = event.x
    event.subject.fy = event.y
  }

  const dragEnded = (event: any) => {
    if (!event.active) simulation.alphaTarget(0)
    event.subject.fx = null
    event.subject.fy = null
  }
  return d3.drag().on('start', dragStarted).on('drag', dragged).on('end', dragEnded)
}

/**
 * Provide d values for the arc between the start and end radius based on time
 * @param bubble Bubble data
 */
export const arcTween = (bubble: BubbleData) => {
  // Percentage of the budget
  let p = bubble.transaction_total / bubble.amount
  if (p < 0) p = 0
  else if (p > 1) p = 1

  // End angle for the progress status
  const angle = p * 2 * Math.PI
  // Interpolate values between 0 and the end angle
  const interpolate = d3.interpolate(0, angle)
  // Progress arc starting at 0
  const arc = d3
    .arc<BubbleData>()
    .cornerRadius(5)
    .innerRadius(bubble.radius - PROGRESS_INDICATOR_SIZE * 2)
    .outerRadius(bubble.radius - PROGRESS_INDICATOR_SIZE)
    .startAngle(0)
    .endAngle(0)

  return (t: number) => {
    // assign end angle to interpolated value for t
    arc.endAngle(interpolate(t))
    // call arc and return intermediate `d` value
    return arc(bubble) || ''
  }
}

/**
 * Enable drag
 * @param simulation D3 Simulation
 */
export const enableDrag = (simulation: SimulationType) => {
  d3.selectAll('.bubble').call(drag(simulation) as any)
}

/**
 * Clamps a number between a minimum and maximum value.
 *
 * @param value - The number to be clamped.
 * @param min - The minimum allowable value.
 * @param max - The maximum allowable value.
 * @returns The clamped value within the range [min, max].
 */
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

/**
 * Update location after a resize
 * @param simulation D3 Simulation
 * @param height New height
 * @param width New width
 */
export const resize = (simulation: SimulationType, height: number, width: number) => {
  simulation
    .alpha(1)
    .restart()
    .force(
      'x',
      d3
        .forceX()
        .x(width / 2)
        .strength(0.01),
    )
    .force(
      'y',
      d3
        .forceY()
        .y(height / 2)
        .strength(0.02),
    )
    .on('tick', () => {
      const nodes = simulation.nodes()

      nodes.forEach((node: BubbleData) => {
        const radius = node.radius ?? MIN_BUBBLE_RADIUS

        node.x = clamp(node.x ?? 0, radius, width - radius)
        node.y = clamp(node.y ?? 0, radius, height - radius)
      })

      d3.selectAll('.bubble')
        .data(nodes)
        .attr('transform', (d: BubbleData) => `translate(${d.x},${d.y})`)
    })
}

export const getScale = (min: number, max: number) => {
  return d3.scaleLinear().domain([min, max]).range([MIN_BUBBLE_RADIUS, MAX_BUBBLE_RADIUS])
}

/**
 * Simulates the chart
 * @param budgets Budget data
 */
export const simulate = (budgets: DetailedBudget[]) => {
  const maxValue = budgets.reduce((acc, budget) => {
    const max = Math.max(budget.transaction_total, budget.amount)
    return max > acc ? max : acc
  }, 0)

  const minValue = budgets.reduce((acc, budget) => {
    const max = Math.max(budget.transaction_total, budget.amount)
    return max < acc ? max : acc
  }, maxValue)

  const scale = getScale(minValue, maxValue)

  const bubbles = budgets.map((budget) => {
    const radius = scale(Math.max(budget.transaction_total, budget.amount)) || 25

    return {
      ...budget,
      radius,
    } as BubbleData
  })

  return d3
    .forceSimulation<BubbleData>(bubbles)
    .velocityDecay(0.05)
    .force('collide', d3.forceCollide<BubbleData>((d) => d.radius + 2).strength(0.7))
}

/**
 * Calculate the target radius based on the number of bubbles and container size
 * @param count Number of bubbles
 * @param height Height of the container
 * @param width Width of the container
 * @returns An object containing the target minimum and maximum radius
 */
const getTargetRadius = (bubbleCount: number, containerHeight: number, containerWidth: number) => {
  const minBubbleArea = Math.PI * MIN_BUBBLE_RADIUS ** 2
  const totalMinBubbleArea = minBubbleArea * bubbleCount

  const isSmallContainer = containerWidth < 400 || containerHeight < 300
  const padding = isSmallContainer ? 40 : 20
  const usableWidth = Math.max(100, containerWidth - padding)
  const usableHeight = Math.max(100, containerHeight - padding)
  const usableArea = usableWidth * usableHeight

  let maxRadius = MIN_BUBBLE_RADIUS

  if (totalMinBubbleArea < usableArea) {
    const radiusFromArea = Math.sqrt(usableArea / (bubbleCount * Math.PI))
    const radiusFromDimensions = Math.min(usableWidth, usableHeight) / 4

    maxRadius = Math.min(MAX_BUBBLE_RADIUS, radiusFromArea, radiusFromDimensions)
  }

  if (isSmallContainer && bubbleCount <= 3) {
    maxRadius = Math.min(maxRadius, 45)
  }

  const minRadius = Math.max(20, maxRadius * 0.6)

  return { minRadius, maxRadius }
}

/* @param count Number of bubbles
 * @param height Height of the container
 * @param maxValue Maximum value for the scale
 * @param minValue Minimum value for the scale
 * @param width Width of the container
 * @returns A D3 linear scale for bubble radius
 */
export const getSmartScale = (
  count: number,
  height: number,
  maxValue: number,
  minValue: number,
  width: number,
) => {
  const { maxRadius, minRadius } = getTargetRadius(count, height, width)

  return d3.scaleLinear().domain([minValue, maxValue]).range([minRadius, maxRadius])
}

export const computeScaledNodes = (
  nodes: BubbleData[],
  budgets: DetailedBudget[],
  isCompact: boolean = false,
  height: number,
  width: number,
) => {
  const values = budgets.map((b) => Math.max(b.transaction_total, b.amount))
  const min = Math.min(...values)
  const max = Math.max(...values)

  const scale = isCompact
    ? getSmartScale(nodes.length, height, max, min, width)
    : getScale(min, max)

  return nodes.map((node) => ({
    ...node,
    radius: scale(Math.max(node.transaction_total, node.amount)),
  }))
}
